import * as go from 'gojs';
import {
  convertToCurrentMetric,
  CONVERSIONS,
  getMetricUnit,
  roundToXDecimals,
} from 'util/conversions';
import { linkOutputFlowPanel } from '../nodeTemplates/Panels/linkOutputFlowPanel';
import { hover, unhover } from '../utils';
import theme from 'theme/theme';

const $ = go.GraphObject.make;

const setFromSpot = (x: number, y: number) => {
  if (x === 0 && y > 0) {
    return go.Spot.Left;
  } else if (x === 1 && y > 0 && y < 0.9) {
    return go.Spot.Right;
  } else if (y === 0 && x > 0.1 && x < 0.9) {
    return go.Spot.Top;
  } else if (y === 1 && x > 0 && x < 0.9) {
    return go.Spot.Bottom;
  } else {
    return go.Spot.AllSides;
  }
};

// this is shown by the mouseHover event handler
const nodeHoverAdornment = $(
  go.Adornment,
  'Link',
  {
    background: 'transparent',
  },
  $(go.Placeholder, {
    background: 'transparent', // to allow this Placeholder to be "seen" by mouse events
  }),
  $(
    go.Panel,
    'Auto',
    $(go.Shape, 'RoundedRectangle', {
      alignment: go.Spot.Center,
      fill: '#E6F5FF95',
      stroke: '#327DFF',
      strokeWidth: 2,
    }),
    $(
      go.Panel,
      'Vertical',
      $(
        go.TextBlock,
        {
          font: 'normal normal 500 13px Sandvik Sans Text',
          alignment: go.Spot.Left,
        },
        new go.Binding('text', '', (data) => {
          const convertedFlowRate = convertToCurrentMetric({
            value: data.flowRate,
            metricType: data.metricType,
            conversionType: CONVERSIONS.CAPACITY,
            decimals: 0,
          });

          const currentCapacityUnit = getMetricUnit(
            data.metricType,
            CONVERSIONS.CAPACITY,
          );

          return `Flow rate: ${convertedFlowRate} ${currentCapacityUnit}`;
        }),
      ),
      $(
        go.TextBlock,
        {
          font: 'normal normal 500 13px Sandvik Sans Text',
          alignment: go.Spot.Left,
        },
        new go.Binding('text', '', (data) => {
          if (
            typeof data.lowerFractionSize !== 'undefined' &&
            typeof data.upperFractionSize !== 'undefined' &&
            typeof data.topSize !== 'undefined'
          ) {
            const convertedLowerFractionSize = convertToCurrentMetric({
              value: data.lowerFractionSize,
              metricType: data.metricType,
              conversionType: CONVERSIONS.MILLIMETER,
            });

            const convertedUpperFractionSize = convertToCurrentMetric({
              value: data.upperFractionSize,
              metricType: data.metricType,
              conversionType: CONVERSIONS.MILLIMETER,
            });

            const convertedTopSize = convertToCurrentMetric({
              value: data.topSize,
              metricType: data.metricType,
              conversionType: CONVERSIONS.MILLIMETER,
            });

            const currentMetric = getMetricUnit(
              data.metricType,
              CONVERSIONS.MILLIMETER,
            );

            if (
              typeof convertedTopSize !== 'undefined' &&
              typeof convertedUpperFractionSize !== 'undefined'
            ) {
              if (convertedTopSize > convertedUpperFractionSize) {
                return `Size: ${convertedLowerFractionSize} - ${convertedUpperFractionSize} (${convertedTopSize}) ${currentMetric}`;
              } else {
                return `Size: ${convertedLowerFractionSize} - ${convertedUpperFractionSize} ${currentMetric}`;
              }
            }
          }
        }),
      ),
      $(
        go.TextBlock,
        {
          font: 'normal normal 500 13px Sandvik Sans Text',
          alignment: go.Spot.Left,
        },
        new go.Binding('text', '', (data) => {
          return `Moisture: ${roundToXDecimals(data.flowMoisture, 1)} %`;
        }),
      ),
    ),
  ),
);

const getLinkStroke = (data: { color?: string; outputFlow?: number }) => {
  if (data.color) {
    return data.color;
  }
  return data.outputFlow === 0
    ? theme.palette.secondary.main
    : theme.palette.info.main;
};

/**
 * A default link template for the connection between units
 */
export const DefaultLinkTemplate = $(
  go.Link,
  { layerName: '' },
  {
    mouseEnter: (e: any, obj: go.GraphObject) => {
      hover(obj, 'LINKSTROKE');
    },
    mouseLeave: (e: any, obj: go.GraphObject) => {
      unhover(obj, 'LINKSTROKE');
      obj.part?.removeAdornment('mouseHover');
    },
    mouseHover: function (e, obj) {
      const link = obj.part;
      nodeHoverAdornment.adornedObject = link;
      if (link) link.addAdornment('mouseHover', nodeHoverAdornment);
    },
  },
  {
    routing: go.Link.Orthogonal,
    corner: 3,
    curve: go.Link.JumpGap,
    reshapable: true,
    resegmentable: true,
    relinkableFrom: true,
    relinkableTo: true,
    selectionAdornmentTemplate: $(
      go.Adornment,
      'Link',
      $(
        go.Shape,
        { isPanelMain: true, fill: null, strokeWidth: 3 },
        new go.Binding('stroke', '', getLinkStroke),
        new go.Binding('strokeDashArray', 'outputFlow', (outputFlow) =>
          outputFlow === 0 ? [3, 2] : null,
        ),
      ),
    ),
  },
  new go.Binding('adjusting', 'adjusting'),
  new go.Binding('points').makeTwoWay(),
  new go.Binding('fromSpot', '', function (e, link) {
    let name = link.fromPort.name;

    if (name.toString().startsWith('D')) {
      name = 'SCREENOUTPUTPORT';
    }

    let align: go.Spot;
    switch (name) {
      case 'SCREENOUTPUTPORT':
        align = link.fromPort.panel.panel.panel.panel.panel.alignment;
        return setFromSpot(align.x, align.y);
      case 'OUTPUTPORT':
        align = link.fromPort.panel.panel.panel.alignment;
        return setFromSpot(align.x, align.y);
      default:
        break;
    }
  }).ofObject(),
  $(
    go.Shape,
    {
      fill: theme.palette.info.main,
      stroke: theme.palette.info.main,
      strokeWidth: 1,
      isPanelMain: true,
    },

    new go.Binding('stroke', '', getLinkStroke),
    new go.Binding('strokeDashArray', 'outputFlow', (outputFlow) =>
      outputFlow === 0 ? [3, 2] : null,
    ),
  ),
  $(go.Shape, {
    isPanelMain: true,
    stroke: null,
    strokeWidth: 4,
    name: 'LINKSTROKE',
  }),

  $(
    go.Panel,
    'Auto',
    {
      visible: false,
      _isLinkLabel: true,
      segmentIndex: NaN,
      segmentOffset: new go.Point(NaN, 0),
    },
    new go.Binding('segmentFraction', 'flowRatePosition').makeTwoWay(),
    new go.Binding('visible', 'outputFlow', (outputFlow) => outputFlow >= 0),
    $(
      go.Shape,
      'RoundedRectangle',
      {
        strokeWidth: 0,
        alignment: go.Spot.Bottom,
      },
      new go.Binding('fill', '', function (data) {
        if (!data.massFlows) {
          return 'transparent';
        }
        return theme.palette.info.main;
      }),
    ),
    linkOutputFlowPanel(),
  ),
);
